import React, { useRef, useState, useEffect } from 'react';
import MapFrame from '../../components/MapFrame';
import RSVPForm        from '../../components/RSVPForm';
import Wishlist        from '../../components/Wishlist';
import YandexMap       from '../../components/YandexMap';
import Countdown       from '../../components/Countdown';
import FixedCTA        from '../../components/FixedCTA';
import useReveal       from '../../components/useReveal';
import Schedule        from '../../components/Schedule';
import DresscodePalette from '../../components/DresscodePalette';
import CalendarButton  from '../../components/CalendarButton';
import BlockSubtitle   from '../../components/BlockSubtitle';
import MusicPlayer from '../../components/MusicPlayer';
import { ReactComponent as MapRing } from './map-ring.svg';
import PaidWatermark from '../../components/PaidWatermark';
import './Alarus.css';

/* ── Helpers ─────────────────────────────────────────────────── */
const src = u => !u ? null : u;
const FILTER_CSS = {
  warm:     'sepia(0.3) saturate(1.3) brightness(1.05)',
  cool:     'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
  vintage:  'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.8)',
  bw:       'grayscale(1)',
  dramatic: 'contrast(1.3) brightness(0.9) saturate(1.2)',
  soft:     'brightness(1.1) saturate(0.8) contrast(0.9)',
  golden:   'sepia(0.4) brightness(1.1) saturate(1.4) hue-rotate(-10deg)',
};
const flt = p => (p?.filter && p.filter !== 'none' ? FILTER_CSS[p.filter] : '') || undefined;
const objPos = p => (p && Number.isFinite(p.focalX) && Number.isFinite(p.focalY))
  ? `${(p.focalX * 100).toFixed(1)}% ${(p.focalY * 100).toFixed(1)}%`
  : undefined;

const fmtDateSlash = raw => {
  if (!raw) return '??/??/????';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return '??/??/????';
  return [d.getDate(), d.getMonth() + 1, d.getFullYear()]
    .map((n, i) => i < 2 ? String(n).padStart(2, '0') : String(n)).join('/');
};

/* Load Monomakh (Old-Slavonic display) + Open Sans once. */
function useAlarusFonts() {
  useEffect(() => {
    const id = 'alr-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Monomakh&family=Open+Sans:wght@300;400;600;700&display=swap';
    document.head.appendChild(link);
  }, []);
}

/* ── Class maps for shared components ─────────────────────── */
const CD_CLASSES    = { root: 'alr-cd',    cell: 'alr-cd-cell', num: 'alr-cd-num', label: 'alr-cd-lbl' };
const SCHED_CLASSES = { root: 'alr-sched', row: 'alr-sched-row', rowBorder: 'alr-sched-row--border', time: 'alr-sched-time', info: 'alr-sched-info', name: 'alr-sched-name', desc: 'alr-sched-desc' };
const DRESS_CLASSES = { wrap: 'alr-swatches', swatchWrap: 'alr-swatch-wrap', swatch: 'alr-swatch', swatchLabel: 'alr-swatch-lbl', photos: 'alr-dc-photos', photo: 'alr-dc-photo' };
const RSVP_CLASSES = {
  form:    'alr-form',
  tabs:    'alr-rsvp-tabs',
  tab:     'alr-rsvp-tab',
  tabSel:  'alr-rsvp-tab--sel',
  field:   'alr-form-field',
  label:   'alr-form-lbl',
  input:   'alr-form-input',
  submit:  'alr-btn alr-btn--primary alr-eagle-btn',
  success: 'alr-form-ok',
  error:   'alr-form-err',
  chip:    'alr-chip',
  chipSel: 'alr-chip--sel',
  chips:   'alr-form-chips',
};
const WISHLIST_CLASSES = {
  list:        'alr-wish',
  item:        'alr-wish-item',
  itemBorder:  'alr-wish-item--border',
  row:         'alr-wish-row',
  name:        'alr-wish-name',
  price:       'alr-wish-price',
  btn:         'alr-btn alr-btn--primary alr-eagle-btn',
  btnReserved: 'alr-btn--res',
};
const WISHLIST_MODAL_CLASSES = {
  bg:         'alr-wish-modal-bg',
  modal:      'alr-wish-modal',
  title:      'alr-wish-modal-title',
  subtitle:   'alr-wish-modal-sub',
  input:      'alr-form-input',
  error:      'alr-form-err',
  btns:       'alr-wish-modal-btns',
  btnCancel:  'alr-btn alr-btn--outline',
  btnConfirm: 'alr-btn alr-btn--primary',
};

const A = '/templates/alarus';

/* ══ Main Template ═════════════════════════════════════════════ */
export default function Alarus({ project, design, isPreview }) {
  const pageRef  = useRef(null);
  const blockDep = project?.blocks?.length;
  useReveal(pageRef, blockDep, { revealSelector: '.alr-reveal', inClass: 'alr-in' });
  useAlarusFonts();

  const [inPreview, setInPreview] = useState(!!isPreview);
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    setInPreview(!!isPreview || !!root.closest('.lp-phone-screen,.lp-template-inner,[class*="lp-"]'));
  }, [isPreview]);

  const p = project || {};
  const d = design  || {};

  /* Colours — palette override but keep А-ля-рус red/blue as default. */
  const accent = d.palette?.primary   || p.palette?.primary   || '#FB2C33';
  const bg     = d.palette?.secondary || p.palette?.secondary || '#E7F7FB';
  const fg     = d.palette?.textDark  || p.palette?.textDark  || '#1E1E1E';

  /* Blocks */
  const blocks = p.blocks || ['menu','map','schedule','palette','rsvp','survey','message','wishlist','countdown','calendar'];
  const HAS_MENU     = blocks.includes('menu');
  const HAS_CALENDAR = blocks.includes('calendar');

  /* Text */
  const bride       = p.bride || '';
  const groom       = p.groom || '';
  const tagline     = p.block_content?.header?.subtitle || '';
  const invText     = p.invitation_text || p.block_content?.message?.text || '';
  const invTitle    = p.block_content?.message?.title || 'Дорогие гости';
  const footerTitle = p.block_content?.footer?.quote || 'Спасибо, что вы с нами';

  /* Photos */
  const photos        = p.cover_photos || (p.cover_photo ? [{ url: p.cover_photo }] : []);
  const heroPhoto     = src(photos[0]?.url) || null;
  const heroPhotoMeta = photos[0] || null;
  const dcPhotos      = p.dresscode_photos || [];
  const footerPhotoObj = p.footer_photo?.url ? p.footer_photo : null;
  const footerPhoto    = src(footerPhotoObj?.url) || null;

  /* Date */
  const dateStr = fmtDateSlash(p.wedding_date);

  /* Data */
  const schedule = p.schedule?.length ? p.schedule : [];
  const venues   = p.venues || [];
  const swatches = p.dresscode_palette || [];
  const wishlist = p.wishlist || [];

  /* Block content titles */
  const bc = p.block_content || {};
  const titleLocation = bc.map?.title       || 'Локация';
  const titleSchedule = bc.schedule?.title  || 'Программа';
  const titleDress    = bc.palette?.title   || 'Дресс-код';
  const titleRsvp     = bc.rsvp?.title      || 'Подтвердите присутствие';
  const titleWish     = bc.wishlist?.title  || 'Что дарить';
  const titleCD       = bc.countdown?.title || 'До нашей свадьбы осталось';
  const calBtn        = bc.calendar?.title  || 'Добавить в календарь';

  return (
    <div className={`alr-page${inPreview ? ' alr-page--preview' : ''}`} ref={pageRef}
      style={{
        '--alr-accent': accent, '--alr-bg': bg, '--alr-fg': fg,
        '--alr-arch':  `url(${A}/arch.svg)`,
        '--alr-pin':   `url(${A}/pin.svg)`,
        '--alr-star':  `url(${A}/star.svg)`,
        '--alr-eagle': `url(${A}/eagle.svg)`,
      }}>
      <PaidWatermark isPaid={project?.is_paid} isPreview={inPreview} />

      {/* Navigation */}
      {HAS_MENU && (
        <nav className="alr-nav">
          {blocks.includes('schedule') && <a href="#alr-schedule">{bc.menu?.schedule || 'Программа'}</a>}
          {blocks.includes('map')      && <a href="#alr-location">{bc.menu?.location || 'Локация'}</a>}
          {blocks.includes('palette')  && <a href="#alr-dresscode">{bc.menu?.dresscode || 'Дресс-код'}</a>}
          {blocks.includes('rsvp')     && <a href="#alr-rsvp">{bc.menu?.rsvp || 'Анкета'}</a>}
        </nav>
      )}

      {/* Hero — names + tagline + ornamental-framed photo */}
      <section className="alr-hero alr-panel alr-panel--btm alr-reveal" data-nophoto={heroPhoto ? undefined : '1'}>
        <div className="alr-hero-names">
          {bride && <span className="alr-hero-name">{bride}</span>}
          <span className="alr-hero-name alr-hero-amp">{bc.header?.amp || 'да'}</span>
          {groom && <span className="alr-hero-name">{groom}</span>}
        </div>
        {tagline && <p className="alr-hero-tagline">{tagline}</p>}
        {heroPhoto && (
          <div className="alr-hero-frame">
            <div className="alr-hero-photo">
              <img src={heroPhoto} alt="" style={{ filter: flt(heroPhotoMeta), objectPosition: objPos(heroPhotoMeta) }} />
            </div>
            <img className="alr-hero-frame-img" src={`${A}/hero-frame.png`} alt="" aria-hidden="true" />
          </div>
        )}
      </section>

      {/* Invitation letter */}
      <section className="alr-pad alr-invite alr-reveal">
        <h2 className="alr-invite-title">{invTitle}</h2>
        <div className="alr-invite-body">
          {invText
            ? invText.split('\n').map((line, i) =>
                line.trim() ? <p key={i}>{line}</p> : <br key={i} />)
            : null}
        </div>
      </section>

      {/* Date panel — samovar + date + calendar */}
      <section className="alr-date-sect alr-panel alr-panel--top alr-panel--btm alr-reveal">
        <img className="alr-samovar" src={`${A}/samovar.png`} alt="" aria-hidden="true" />
        <p className="alr-date">{dateStr}</p>
        {HAS_CALENDAR && (
          <CalendarButton
            bride={bride}
            groom={groom}
            date={p.wedding_date}
            time={p.wedding_time}
            venues={p.venues}
            address={p.wedding_address}
            className="alr-btn alr-btn--outline alr-cal-btn alr-eagle-btn">
            {calBtn}
          </CalendarButton>
        )}
      </section>

      {/* Dynamic blocks — Figma order */}
      {['map','schedule','palette','rsvp','wishlist','countdown']
        .filter(b => blocks.includes(b))
        .map(blockId => {
          switch (blockId) {
            case 'map':
              return (
                <section key="map" className="alr-pad alr-map-sect alr-reveal" id="alr-location">
                  <h2 className="alr-h">{titleLocation}</h2>
                  <BlockSubtitle text={bc.map?.subtitle} className="alr-p" />
                  <div className="alr-addr">
                    <span className="alr-addr-pin" aria-hidden="true" />
                    <span className="alr-addr-text">
                      {venues.filter(v => v.address).length > 0
                        ? venues.filter(v => v.address).flatMap((v, i, arr) => {
                            const line = <span key={`a${i}`} style={{ display: 'block' }}>{v.name && v.address ? `${v.name}, ${v.address}` : (v.name || v.address)}</span>;
                            return i < arr.length - 1
                              ? [line, <span key={`d${i}`} style={{ display: 'block', textAlign: 'center', opacity: 0.5, margin: '6px 0' }}>•</span>]
                              : [line];
                          })
                        : (p.wedding_address || '')}
                    </span>
                  </div>
                  {/* Карта — только при наличии адреса (имя без адреса → без карты). */}
                  {(p.map_url || (p.wedding_address || '').trim() || venues.some(v => (v.address || '').trim())) && (
                  <div className="alr-map-wrap">
                    <div className="alr-map-circle">
                      {(() => {
                        if (p.map_url) {
                          return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={p.map_url} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                        }
                        const withCoords = venues.filter(v => Number.isFinite(+v.lat) && Number.isFinite(+v.lon));
                        if (withCoords.length > 0) {
                          return <YandexMap points={withCoords} singleZoom={16} region={p.region} provider={bc.map?.provider} preset="islands#redIcon" />;
                        }
                        const addr = venues.length > 0
                          ? venues.map(v => v.address).filter(Boolean).join(' — ')
                          : (p.wedding_address || '');
                        if (!addr) return null;
                        const s = `https://yandex.ru/map-widget/v1/?mode=search&text=${encodeURIComponent(addr)}&z=16&lang=ru_RU`;
                        return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={s} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
                      })()}
                    </div>
                    <MapRing className="alr-map-ring" aria-hidden="true" />
                  </div>
                  )}
                </section>
              );

            case 'schedule':
              return (
                <section key="schedule" className="alr-pad alr-sched-sect alr-panel alr-panel--top alr-panel--btm alr-reveal" id="alr-schedule">
                  <h2 className="alr-h alr-h--light">{titleSchedule}</h2>
                  <BlockSubtitle text={bc.schedule?.subtitle} className="alr-p alr-p--light" />
                  <Schedule items={schedule} classes={SCHED_CLASSES} />
                </section>
              );

            case 'palette':
              return (
                <section key="palette" className="alr-pad alr-dress-sect alr-reveal" id="alr-dresscode">
                  <h2 className="alr-h">{titleDress}</h2>
                  <BlockSubtitle text={bc.palette?.subtitle} className="alr-p" />
                  <DresscodePalette
                    swatches={swatches}
                    photos={dcPhotos}
                    cssFilter={flt}
                    classes={DRESS_CLASSES}
                  />
                </section>
              );

            case 'rsvp':
              return (
                <section key="rsvp" className="alr-pad alr-rsvp-sect alr-panel alr-panel--top alr-panel--btm alr-reveal" id="alr-rsvp">
                  <h2 className="alr-h alr-h--light">{titleRsvp}</h2>
                  <BlockSubtitle text={bc.rsvp?.subtitle} className="alr-p alr-p--light" />
                  <RSVPForm project={p} blocks={blocks} classes={RSVP_CLASSES} />
                </section>
              );

            case 'wishlist':
              return (
                <section key="wishlist" className="alr-pad alr-wish-sect alr-reveal">
                  <h2 className="alr-h">{titleWish}</h2>
                  <BlockSubtitle text={bc.wishlist?.subtitle} className="alr-p" />
                  <Wishlist
                    items={wishlist} currency={bc.wishlist?.currency} reserveText={bc.wishlist?.reserveText}
                    slug={p.slug}
                    classes={WISHLIST_CLASSES}
                    modalClasses={WISHLIST_MODAL_CLASSES}
                  />
                </section>
              );

            case 'countdown':
              return (
                <section key="countdown" className="alr-pad alr-cd-sect alr-reveal">
                  <h2 className="alr-h">{titleCD}</h2>
                  <BlockSubtitle text={bc.countdown?.subtitle} className="alr-p" />
                  <Countdown date={p.wedding_date} time={p.wedding_time} bc={bc} classes={CD_CLASSES} />
                </section>
              );

            default:
              return null;
          }
        })}

      {/* Footer */}
      <footer className="alr-footer alr-panel alr-panel--top alr-reveal">
        <h2 className="alr-footer-title">{footerTitle}</h2>
        {footerPhoto && (
          <div className="alr-footer-photo">
            <img src={footerPhoto} alt="" style={{ filter: flt(footerPhotoObj), objectPosition: objPos(footerPhotoObj) }} />
          </div>
        )}
      </footer>

      {/* Floating RSVP CTA */}
      {blocks.includes('rsvp') && (
        <FixedCTA
          href="#alr-rsvp"
          targetId="alr-rsvp"
          scrollRoot={pageRef}
          className="alr-fixed-cta">
          {bc.rsvp?.ctaText || 'Подтвердить присутствие'}
        </FixedCTA>
      )}

      {(project?.blocks || []).includes('music') && project?.music?.track?.url && (
        <MusicPlayer music={project.music} palette={project.palette} isPreview={inPreview} />
      )}
    </div>
  );
}
